import { useRef } from "react";
import { Phone, MapPin, ArrowRight, Home, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import FoerderRechner from "../components/foerder-rechner/FoerderRechner";
import NEKOLogo from "../components/shared/NEKOLogo";
import { track } from "../lib/tracking";

const HIGHLIGHTS = [
  "BEG-Förderung bis zu 70% der Investitionskosten",
  "KfW 458 — wir beantragen alles für Sie",
  "Wärmepumpe + Elektro aus einer Hand",
];

export default function WaermepumpeLandingPage() {
  const calcRef = useRef<HTMLElement>(null);

  function scrollToCalc() {
    track("cta_click", { page: "waermepumpe", action: "scroll_to_calc" });
    calcRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div className="min-h-screen bg-neko-dark text-neko-white font-raleway">

      {/* ── NAV ───────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.06]">
        <NEKOLogo size="sm" />
        <a
          href="tel:+4974206929861"
          onClick={() => track("phone_click", { page: "waermepumpe", location: "nav" })}
          className="flex items-center gap-2 text-xs font-[300] tracking-widest uppercase text-white/40 hover:text-neko-gold transition-colors"
        >
          <Phone className="w-3.5 h-3.5" />
          +49 7420 6929861
        </a>
      </div>

      {/* ── HERO ──────────────────────────────────────────────────── */}
      <section className="px-6 py-20 md:py-28">
        <motion.div
          className="max-w-2xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Highlight-Badge */}
          <div className="inline-flex items-center gap-2 mb-8 px-4 py-1.5 border border-neko-gold/30">
            <Home className="w-3 h-3" style={{ color: "#f0a830" }} />
            <span className="text-[10px] tracking-[0.25em] uppercase font-[300]" style={{ color: "#f0a830" }}>
              BEG-Förderung bis 70% — jetzt prüfen
            </span>
          </div>

          <h1 className="font-[200] text-3xl md:text-5xl tracking-[0.05em] leading-relaxed text-white/90 mb-6">
            Wärmepumpe statt<br />Gasheizung — Region Rottweil
          </h1>
          <p className="font-[300] text-sm md:text-base tracking-wider text-white/40 leading-loose max-w-lg mx-auto mb-10">
            Prüfen Sie jetzt Ihre individuelle Förderung nach BEG und KfW —
            kostenlos, unverbindlich, in wenigen Klicks.
          </p>

          <ul className="flex flex-col items-start gap-3 max-w-xs mx-auto mb-12 text-left">
            {HIGHLIGHTS.map((h) => (
              <li key={h} className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#f0a830" }} />
                <span className="font-[300] text-xs tracking-wider text-white/50">{h}</span>
              </li>
            ))}
          </ul>

          <button
            onClick={scrollToCalc}
            className="group inline-flex items-center gap-3 px-10 py-5 border border-neko-gold/70 text-neko-gold hover:bg-neko-gold hover:text-neko-dark transition-all duration-300 text-[11px] tracking-[0.2em] uppercase font-[300]"
          >
            Förderung jetzt berechnen
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </motion.div>
      </section>

      {/* ── RECHNER ───────────────────────────────────────────────── */}
      <section ref={calcRef} className="px-6 pb-20 border-t border-white/[0.06] pt-16">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <div className="w-8 h-px bg-neko-gold/40 mx-auto mb-6" />
            <h2 className="font-[200] text-xl tracking-[0.1em] text-white/80">
              Förderung berechnen
            </h2>
          </div>
          <FoerderRechner />
        </div>
      </section>

      {/* ── FOOTER ────────────────────────────────────────────────── */}
      <footer className="border-t border-white/[0.06] py-6 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-white/25 text-xs font-[300] tracking-widest">
            <MapPin className="w-3.5 h-3.5" />
            <span>Im Bettinger 17 · 78652 Deißlingen</span>
          </div>
          <div className="flex gap-6 text-[10px] font-[300] tracking-[0.22em] uppercase text-white/20">
            <a href="https://www.neko-bws.de/impressum" target="_blank" rel="noopener noreferrer" className="hover:text-white/50 transition-colors">Impressum</a>
            <a href="https://www.neko-bws.de/datenschutz" target="_blank" rel="noopener noreferrer" className="hover:text-white/50 transition-colors">Datenschutz</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
